import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { useIsMobile } from '../hooks/useIsMobile';
import useStore from '../stores/useStore';
import { getDeviceToken, setStaffToken } from '../lib/authToken';

// Animated Flame Logo Component
const FlameLogo = () => (
    <div className="relative w-24 h-28 mx-auto" aria-hidden="true">
        {/* Outer glow */}
        <div className="absolute inset-0 bg-ember-500/30 rounded-full blur-2xl animate-glow-pulse" />

        {/* Flame SVG */}
        <svg
            viewBox="0 0 64 80"
            className="w-full h-full relative z-10 drop-shadow-2xl"
            fill="none"
            role="img"
            aria-hidden="true"
        >
            {/* Main flame */}
            <path
                d="M32 0C32 0 8 24 8 48C8 64 18 76 32 80C46 76 56 64 56 48C56 24 32 0 32 0Z"
                className="fill-gradient-ember"
                fill="url(#flameGradient)"
            />
            {/* Inner flame */}
            <path
                d="M32 20C32 20 18 36 18 52C18 62 24 70 32 72C40 70 46 62 46 52C46 36 32 20 32 20Z"
                fill="url(#innerFlameGradient)"
            />
            {/* Core */}
            <path
                d="M32 40C32 40 26 48 26 56C26 62 28 66 32 68C36 66 38 62 38 56C38 48 32 40 32 40Z"
                fill="url(#coreGradient)"
            />

            <defs>
                <linearGradient id="flameGradient" x1="32" y1="0" x2="32" y2="80" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="50%" stopColor="#E84A27" />
                    <stop offset="100%" stopColor="#991B1B" />
                </linearGradient>
                <linearGradient id="innerFlameGradient" x1="32" y1="20" x2="32" y2="72" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#FCD34D" />
                    <stop offset="100%" stopColor="#F97316" />
                </linearGradient>
                <linearGradient id="coreGradient" x1="32" y1="40" x2="32" y2="68" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#FEF3C7" />
                    <stop offset="100%" stopColor="#FBBF24" />
                </linearGradient>
            </defs>
        </svg>
    </div>
);

export default function LoginScreen({ onLogin }) {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const pinDisplayRef = useRef(null);
    const errorRef = useRef(null);
    const { isMobile } = useIsMobile();
    const [isEnrolled] = useState(() => Boolean(getDeviceToken()));

    const { login, setActiveShift } = useStore();

    const handleNumberClick = useCallback((num) => {
        setPin((prev) => {
            if (prev.length >= 6) return prev;
            return prev + num;
        });
        setError('');
    }, []);

    const handleClear = useCallback(() => {
        setPin('');
        setError('');
    }, []);

    const handleBackspace = useCallback(() => {
        setPin(prev => prev.slice(0, -1));
        setError('');
    }, []);

    const handleDemoLogin = useCallback(() => {
        const demoUser = {
            id: `demo_${pin}`,
            employeeId: `EMP${pin}`,
            name: pin === '0000' ? 'Sarah Mitchell (Manager)' :
                  pin === '4001' ? 'Chef Marcus Webb' :
                  `Server ${pin}`,
            role: pin === '0000' ? 'manager' :
                  pin === '4001' ? 'kitchen' :
                  pin.startsWith('2') ? 'bartender' :
                  pin.startsWith('3') ? 'host' : 'server',
            hourlyRate: 15.50
        };

        login(demoUser, `demo_session_${Date.now()}`);
        onLogin(demoUser);
    }, [login, onLogin, pin]);

    const handleSubmit = useCallback(async () => {
        if (pin.length === 0) {
            setError('Please enter your PIN');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // The device token picks this device's restaurant (spec D2); without
            // one the server answers as the sample tenant.
            const deviceToken = getDeviceToken();
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(deviceToken ? { Authorization: `Bearer ${deviceToken}` } : {})
                },
                body: JSON.stringify({ pin })
            });

            const data = await response.json();

            if (data.success) {
                setStaffToken(data.token);
                login(data.employee, data.token || data.sessionToken);
                if (data.activeShift) {
                    setActiveShift(data.activeShift);
                }
                onLogin(data.employee);
            } else {
                setError(data.code === 'TOKEN_INVALID'
                    ? 'This device\'s owner sign-in has expired. Sign in as owner to set it up again.'
                    : (data.error || 'Invalid PIN'));
                setPin('');
            }
        } catch (err) {
            console.error('Login error:', err);
            handleDemoLogin();
        } finally {
            setIsLoading(false);
        }
    }, [handleDemoLogin, login, onLogin, pin, setActiveShift]);

    // Handle physical keyboard input for PIN entry
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isLoading) return;

            if (e.key >= '0' && e.key <= '9') {
                e.preventDefault();
                handleNumberClick(parseInt(e.key));
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                handleBackspace();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
            } else if (e.key === 'Escape' || e.key === 'Delete') {
                e.preventDefault();
                handleClear();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleBackspace, handleClear, handleNumberClick, handleSubmit, isLoading]);

    const handleQuickLogin = async (role) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/demo-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role })
            });

            const data = await response.json();

            if (data.success) {
                setStaffToken(data.token);
                login(data.employee, data.token || data.sessionToken);
                if (data.activeShift) {
                    setActiveShift(data.activeShift);
                }
                onLogin(data.employee);
            }
        } catch {
            const roles = {
                server: { name: 'Maria Garcia', pin: '1234' },
                manager: { name: 'Sarah Mitchell', pin: '0000' },
                kitchen: { name: 'Chef Marcus Webb', pin: '4001' },
                bartender: { name: 'Nicole Martinez', pin: '2001' }
            };
            const demoUser = {
                id: `demo_${role}`,
                employeeId: roles[role]?.pin || '0000',
                name: roles[role]?.name || 'Demo User',
                role,
                hourlyRate: 15.50
            };
            login(demoUser, `demo_session_${Date.now()}`);
            onLogin(demoUser);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="min-h-full flex flex-col items-center justify-center bg-smoke-950 text-white relative overflow-hidden noise-overlay" aria-label="EmberPOS Login">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                {/* Top ember glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-ember-500/10 rounded-full blur-[100px]" />
                {/* Bottom ambient */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-ember-700/5 to-transparent" />
                {/* Side accents */}
                <div className="absolute top-1/4 -left-20 w-40 h-40 bg-gold-500/10 rounded-full blur-3xl animate-glow-pulse" />
                <div className="absolute bottom-1/4 -right-20 w-40 h-40 bg-ember-500/10 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: '1s' }} />
            </div>

            {/* Logo & Header */}
            <div className={`text-center relative z-10 animate-slide-down ${isMobile ? 'mb-4' : 'mb-8'}`}>
                {!isMobile && <FlameLogo />}
                <h1 className={`font-display font-bold tracking-tight ${isMobile ? 'text-3xl mt-2 mb-1' : 'text-5xl mt-6 mb-2'}`}>
                    <span className="text-gradient-ember">Ember</span>
                    <span className="text-white">POS</span>
                </h1>
                <p className={`text-smoke-500 font-light tracking-wide ${isMobile ? 'text-sm' : 'text-lg'}`}>Hospitality Operating System</p>
            </div>

            {/* Login Card */}
            <div className={`glass-card w-full max-w-md relative z-10 animate-slide-up shadow-2xl ${isMobile ? 'p-5 mx-3' : 'p-8'}`} role="form" aria-label="PIN login">
                {/* Card glow effect */}
                <div className="absolute -inset-px bg-gradient-to-b from-ember-500/20 to-transparent rounded-2xl pointer-events-none" aria-hidden="true" />

                {/* PIN Display */}
                <div className="relative mb-6">
                    <div className="absolute -inset-1 bg-gradient-to-r from-ember-500/20 via-gold-500/20 to-ember-500/20 rounded-xl blur opacity-50" aria-hidden="true" />
                    <label id="pin-label" className="sr-only">PIN code</label>
                    <div
                        ref={pinDisplayRef}
                        className="relative bg-smoke-900 text-white text-4xl font-mono text-center py-5 rounded-xl border border-smoke-700 h-[72px] flex items-center justify-center tracking-[0.5em]"
                        role="status"
                        aria-labelledby="pin-label"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        {pin.length === 0 ? (
                            <span className="text-smoke-600 text-xl tracking-normal font-sans font-light">Enter PIN</span>
                        ) : (
                            <span className="text-gold-400" aria-label={`${pin.length} digits entered`}>{'*'.repeat(pin.length)}</span>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div
                        ref={errorRef}
                        className="bg-red-500/10 border border-red-500/30 text-red-400 text-center py-3 px-4 rounded-xl mb-4 text-sm animate-slide-down"
                        role="alert"
                        aria-live="assertive"
                    >
                        {error}
                    </div>
                )}

                {/* Keypad */}
                <div className={`grid grid-cols-3 ${isMobile ? 'gap-2' : 'gap-3'}`} role="group" aria-label="PIN keypad. You can also type digits on your keyboard.">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                        <button
                            key={num}
                            onClick={() => handleNumberClick(num)}
                            disabled={isLoading}
                            aria-label={`Digit ${num}`}
                            className={`bg-smoke-800 hover:bg-smoke-700 active:bg-smoke-600 font-semibold rounded-xl
                                     transition-all duration-150 disabled:opacity-50 border border-smoke-700 hover:border-smoke-600
                                     shadow-inner-light active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950
                                     ${isMobile ? 'text-xl py-4' : 'text-2xl py-5'}`}
                        >
                            {num}
                        </button>
                    ))}
                    <button
                        onClick={handleClear}
                        disabled={isLoading}
                        aria-label="Clear all digits"
                        className={`bg-smoke-800 hover:bg-red-900/50 active:bg-red-900 font-semibold rounded-xl
                                 transition-all duration-150 disabled:opacity-50 border border-smoke-700 hover:border-red-800
                                 text-red-400 hover:text-red-300 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950
                                 ${isMobile ? 'text-base py-4' : 'text-lg py-5'}`}
                    >
                        CLR
                    </button>
                    <button
                        onClick={() => handleNumberClick(0)}
                        disabled={isLoading}
                        aria-label="Digit 0"
                        className={`bg-smoke-800 hover:bg-smoke-700 active:bg-smoke-600 font-semibold rounded-xl
                                 transition-all duration-150 disabled:opacity-50 border border-smoke-700 hover:border-smoke-600
                                 shadow-inner-light active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950
                                 ${isMobile ? 'text-xl py-4' : 'text-2xl py-5'}`}
                    >
                        0
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || pin.length === 0}
                        aria-label={isLoading ? 'Logging in' : 'Submit PIN'}
                        className={`bg-ember-500 hover:bg-ember-400 active:bg-ember-600 font-bold rounded-xl
                                 transition-all duration-150 disabled:opacity-30 disabled:bg-smoke-700
                                 shadow-lg hover:shadow-ember active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950
                                 ${isMobile ? 'text-base py-4' : 'text-lg py-5'}`}
                    >
                        {isLoading ? (
                            <span className="inline-block animate-spin" aria-hidden="true">&#9676;</span>
                        ) : (
                            'GO'
                        )}
                    </button>
                </div>

                {/* Backspace */}
                <button
                    onClick={handleBackspace}
                    disabled={isLoading || pin.length === 0}
                    aria-label="Delete last digit"
                    className="w-full mt-4 bg-smoke-800/50 hover:bg-smoke-700 text-smoke-500 hover:text-smoke-300
                             py-3 rounded-xl transition-all disabled:opacity-30 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950"
                >
                    <span aria-hidden="true">&larr;</span> Backspace
                </button>
            </div>

            {/* Quick Demo Login */}
            <nav className="mt-10 text-center relative z-10" aria-label="Quick demo access">
                <p className="text-smoke-600 text-sm mb-4 font-medium tracking-wide uppercase">Quick Demo Access</p>
                <div className="flex gap-3 flex-wrap justify-center" role="group" aria-label="Demo login roles">
                    {[
                        { role: 'server', icon: '🍽️', label: 'Server', color: 'from-blue-600/20 to-blue-600/5 border-blue-500/30 hover:border-blue-400/50' },
                        { role: 'manager', icon: '📊', label: 'Manager', color: 'from-purple-600/20 to-purple-600/5 border-purple-500/30 hover:border-purple-400/50' },
                        { role: 'kitchen', icon: '👨‍🍳', label: 'Kitchen', color: 'from-ember-600/20 to-ember-600/5 border-ember-500/30 hover:border-ember-400/50' },
                        { role: 'bartender', icon: '🍸', label: 'Bar', color: 'from-amber-600/20 to-amber-600/5 border-amber-500/30 hover:border-amber-400/50' },
                    ].map(({ role, icon, label, color }) => (
                        <button
                            key={role}
                            onClick={() => handleQuickLogin(role)}
                            disabled={isLoading}
                            aria-label={`Login as demo ${label}`}
                            className={`bg-gradient-to-b ${color} px-5 py-2.5 rounded-xl text-sm font-medium
                                      transition-all duration-200 disabled:opacity-50 border hover:scale-105 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-smoke-950`}
                        >
                            <span className="mr-1.5" aria-hidden="true">{icon}</span>
                            {label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* PIN hint. The demo PINs are the sample kitchen's; on a device
                enrolled to a restaurant only that restaurant's PINs work, and
                this screen can't know them, so it says so instead. */}
            <div className="mt-8 text-smoke-700 text-xs text-center relative z-10">
                {isEnrolled ? (
                    <p>Use a staff PIN for this restaurant. Quick Demo Access opens the sample kitchen.</p>
                ) : (
                    <p>Demo PINs: <span className="text-smoke-500">1234</span> (Server) <span aria-hidden="true">&bull;</span> <span className="text-smoke-500">0000</span> (Manager) <span aria-hidden="true">&bull;</span> <span className="text-smoke-500">4001</span> (Kitchen)</p>
                )}
            </div>
        </main>
    );
}
